/**
 * Scoring models as rule-driven workers (specs/models.md — read it first; this file is its shapes).
 *
 * A MODEL is a worker like a Filter: it has a switch, a RUN RULE (triggers + one condition tree in the
 * ONE condition vocabulary of rules.ts), and it produces topic scores. What happens with the scores is
 * decided by scoring rules ("actions on scoring"), whose score conditions name the model they test.
 * Nothing about how two models combine is hard-wired.
 *
 *   catalogue entry   what a model IS (static data: files, checksums, recipe, calibration, measured quality)
 *   model record      the user's set-up of it (synced row of `models`: switch, order, run rule, options)
 *   install state     what THIS device has of it (never synced: files, download progress, loaded or not)
 *   message score     one row of `message_scores` per (message, model): pending | done | failed
 *
 * Two kinds of model exist:
 *   'remote-jev'      Jev through our proxy (device) or directly (server). Pro only. contracts/scoring.ts.
 *   'onnx-embedding'  a bi-encoder that runs ON THE DEVICE behind `OnDeviceModelRuntime`. The server never
 *                     has one: `EngineDeps.onDeviceModels` is null there, and must stay null.
 *
 * ── How this file relates to the rest of contracts/ ─────────────────────────────────────────────
 * Data schema 3 (models.md §4, §9.2). The "flip list" of the design — the members this change adds to
 * EXISTING declarations, breaking their implementers — was applied by work package M0
 * (specs/_roadmap-models.md) together with schema 3; M1–M5 replaced its stubs and M6 checked that none is
 * left (no "TODO" marker of this change remains in packages/). Applied:
 *  1. sync.ts `SCHEMA_VERSION = 3`; `SYNC_TABLES` += `MODEL_SYNC_TABLES` below; the `topics` register += 'phrases'.
 *  2. engine.ts `Engine.models: ModelsApi` (access levels in engine/access.ts).
 *  3. engine.ts `MessagesApi.rescore(ids, options?: RescoreOptions)`.
 *  4. engine.ts `EngineEvent` += `ModelEngineEvent`.
 *  5. engine.ts `PipelineStatus.models: PipelineModelStatus[]`; `TickReport.scoredByModel`.
 *  6. engine.ts `EngineDeps.onDeviceModels` is REQUIRED (null on the server, always).
 *  7. rules.ts `ConditionNode` += `ScriptCondition`; `QueryUse` += 'model'.
 *  8. errors.ts `LimitName` += 'active_models'; `ErrorCode` += 'model_unavailable' (HTTP 503). The user's
 *     daily Jev budget is NOT a limit name of its own: it is the deployed `scored_per_day` with
 *     `details.budget = true` (quick controls), so the design's 'jev_daily_budget' was dropped.
 *  9. entities.ts: `Topic.phrases`, `Message.modelScores`, `MessageRow.scoredBy` are required.
 * 10. mcp.ts `McpTools` += `McpModelTools`, `McpPrompts` += `McpModelPrompts`, and the widened shapes
 *     named at the end of this file.
 * 11. settings.ts `ScoringSettings` stays as a type but is not engine input any more (models.md §9.3): the
 *     storage layer derives `settings.scoring` from the Jev record and writes it through.
 * The user's daily Jev budget travels as `ScoreRequest.maxPerDay` (the deployed name; the design's
 * `ScoreRequest.budget` does not exist) and is enforced in one place, the scoring proxy.
 */

import type { MutationResult, ListScope } from './engine.js';
import type { Brand, EpochMs, FolderId, IconName, MessageId, RuleId, Score, TopicId } from './ids.js';
import type { GroupNode, RuleAction, SetOperator } from './rules.js';
import type { MessageRow } from './entities.js';
import type { SyncTableSpec } from './sync.js';

// ── Identity ───────────────────────────────────────────────────────────────────────────────────

/**
 * A model's id = its catalogue id: 'jev' | 'e5-small' | … ([a-z0-9-]+, never '|' or '_' at the start).
 * Stable across versions of the model (the pinned version is `ModelCatalogEntry.version`), so rules
 * that name a model survive an upgrade of it. One record per catalogue entry; never two of the same.
 */
export type ModelId = Brand<string, 'ModelId'>;

export const JEV_MODEL_ID = 'jev' as ModelId;
export const E5_SMALL_MODEL_ID = 'e5-small' as ModelId;

export type ModelKind = 'remote-jev' | 'onnx-embedding';

// ── Catalogue (static data; adding a model = adding an entry) ──────────────────────────────────

/** Measured facts shown as the accuracy and speed bars (design fix 6). Never invented: `measuredOn` says how. */
export interface ModelQuality {
  /** Topic-ranking AUC (0.5 = chance, 1 = perfect) with topics described the way the model likes best. */
  topicAuc: number;
  /** The same when a topic is only its NAME; null when that is the model's normal input (Jev). */
  topicAucNamesOnly: number | null;
  /** Typical seconds per message on a laptop (on-device) or per message through the proxy (remote). */
  secondsPerMessage: number;
  /** One line: corpus, date, runtime. Shown under the bars. */
  measuredOn: string;
  /** Honest, measured weaknesses in plain words; shown in the model editor, never hidden. */
  caveats: string[];
}

export interface ModelLanguages {
  /** '100 languages' */
  label: string;
  examples: string[];
  note: string;
}

/**
 * A text field of a Topic that a model may read (models.md §5.0). `name` is every model's minimum;
 * `description` is the column `topics.hint` ("What belongs in this topic?"); `phrases` are the
 * paraphrases. WHICH fields a model reads, and how much of each, is a property of the MODEL — the
 * Topic keeps all of them.
 */
export type TopicField = 'name' | 'description' | 'phrases';

/** What a catalogue model can read from a Topic. */
export interface ModelTopicFields {
  /** Fields the model can read; always contains 'name'. A field not listed is never offered in the model's settings and never given to it. */
  supported: TopicField[];
  /** Fields a fresh model record reads: a subset of `supported` that contains 'name'. */
  default: TopicField[];
  /**
   * The most the model takes of each field. Longer text is cut FOR THIS MODEL ONLY — a Topic is never
   * refused because of one model's limit (the general entity limits still apply). null = not supported.
   */
  limits: { nameChars: number; descriptionChars: number | null; phrases: number | null; phraseChars: number | null };
}

/** What a fresh model record looks like (also what "Use the recommended set-up" writes). */
export interface ModelDefaults {
  enabled: boolean;
  position: number;
  run: ModelRun;
  options: ModelOptions;
  /**
   * The model's own block of the seeded ladder, top to bottom. `minScore` null = "this model scored the
   * message at all" (compiled as best-score ≥ 0). Seeded as scoring rules with these fixed ids.
   */
  ladder: Array<{ ruleId: RuleId; name: string; icon: IconName; minScore: Score | null; action: RuleAction }>;
}

interface ModelCatalogBase {
  id: ModelId;
  kind: ModelKind;
  name: string;
  icon: IconName;
  /** One sentence for the list and the overview card. */
  description: string;
  /** e.g. 'Recommended for everyday reading'; '' = no badge. */
  recommendation: string;
  /**
   * The pinned version written to `message_scores.model_version`: 'jev-1.13.0', or for an embedding
   * model `${dtype}-${revision[0..8]}-c${calibration.version}` ('q8-761b726d-c1'). A change of it means
   * old vectors of this model are stale; old scores stay as history.
   */
  version: string;
  /** The plan feature the model needs: 'aiScoring' = Pro (Jev); null = any plan. */
  requires: 'aiScoring' | null;
  languages: ModelLanguages;
  quality: ModelQuality;
  /** Jev: name only. Multilingual E5 Small: name, description, phrases (default name + phrases). */
  topicFields: ModelTopicFields;
  defaults: ModelDefaults;
}

export interface RemoteJevCatalogEntry extends ModelCatalogBase {
  kind: 'remote-jev';
  /** Equals contracts/scoring.ts JEV_MODEL. */
  jevModel: string;
}

export interface ModelFile {
  /** Path under the model's directory, e.g. 'onnx/model_quantized.onnx'. */
  path: string;
  role: 'weights' | 'tokenizer' | 'tokenizer-config';
  bytes: number;
  /** Lower-case hex. The device refuses a file whose SHA-256 differs. */
  sha256: string;
}

/** How texts become vectors. Vocabulary is code (the runtime implements each value); entries are data. */
export interface EmbeddingRecipe {
  dims: number;
  maxTokens: number;
  /** Prefix for topic texts and anchors ('query: '). */
  queryPrefix: string;
  /** Prefix for messages ('passage: '). */
  passagePrefix: string;
  pooling: 'mean';
  normalize: true;
  tokenizer: 'hf-tokenizers-json';
  /** Message text = title + '\n\n' + the first `bodyMaxChars` characters of htmlToText(body). */
  bodyMaxChars: number;
}

/**
 * "Anchored share" — the calibration that measured best (models.md §5). Deterministic: the same
 * message, topic texts and model files give the same score on every device; no history is needed.
 */
export interface AnchoredShareCalibration {
  method: 'anchored-share';
  /** Bumped when anything below changes; part of `version`. */
  version: number;
  temperature: number;
  /** Generic subjects every topic is measured against. Embedded once per device with `queryPrefix`. */
  anchors: string[];
  /** A topic described by fewer texts than this gets `fewTextsBoost` added to its cosine. */
  fewTextsBelow: number;
  fewTextsBoost: number;
}

export interface EmbeddingCatalogEntry extends ModelCatalogBase {
  kind: 'onnx-embedding';
  license: string;
  /** Where the DEPLOY KIT fetches the files from (never the browser). `url` ends with the pinned revision + '/'. */
  upstream: { repo: string; revision: string; url: string };
  files: ModelFile[];
  /** Sum of `files[].bytes`. */
  downloadBytes: number;
  recipe: EmbeddingRecipe;
  calibration: AnchoredShareCalibration;
  /** Resident memory while loaded, measured (MB). Shown before download. */
  memoryMb: number;
}

export type ModelCatalogEntry = RemoteJevCatalogEntry | EmbeddingCatalogEntry;

/**
 * packages/core/src/schema/model-catalog.ts exports `MODEL_CATALOG: ModelCatalog` (the source of truth);
 * `config/model-catalog.json` is generated from it for the deploy kit (`npm run models:catalog -- --check`).
 */
export interface ModelCatalog {
  /** Bumped on every change of the file. */
  version: number;
  /**
   * URL prefix on the APP origin where files are served: `${servedFrom}${id}/${upstream.revision}/${file.path}`.
   * '/models/'. Same origin as the app, so the CSP needs nothing new.
   */
  servedFrom: string;
  entries: ModelCatalogEntry[];
}

// ── The model record (synced table `models`) ───────────────────────────────────────────────────

/**
 * When a model looks at a message.
 *  - entry / automatic: the message entered `folderId` by its source (arrival in Inbox), a filter or a scoring rule
 *  - entry / manual:    a person or their assistant moved it there
 *  - entry / any:       either
 *  - request:           a person or assistant asked ("Score now", `messages.rescore`, MCP `rescore_messages`)
 * `folderId: 'any'` = every folder (narrow it with a `folder` condition). Moves by `system` never trigger.
 */
export type ModelTrigger =
  | { on: 'entry'; folderId: FolderId | 'any'; by: 'automatic' | 'manual' | 'any' }
  | { on: 'request' };

/**
 * The run rule. The model scores a message when one of `triggers` fires AND `condition` evaluates to
 * `match` (an empty root = always). Fields allowed in `condition` (QueryUse 'model'): folder, source,
 * source-type, title, author, body, tag, read, image, script. NOT allowed: topic, topic-score, best-score
 * (a model does not wait for another model; cascade through folders instead), hours.
 * `body` is `unknown` while content is pending: an automatic entry then waits like a filter does; a
 * manual entry treats unknown as no-match.
 */
export interface ModelRun {
  /** 0..8 triggers; none = the model never runs by itself. Duplicates are rejected. */
  triggers: ModelTrigger[];
  condition: GroupNode;
}

/** Per-model settings. Every key exists for every model; a key a model does not use is ignored. */
export interface ModelOptions {
  /**
   * Characters of body text given to the model (Jev: sent and billed, 1..`scoring.body_max_chars`;
   * on-device: before the 512-token cut). Jev's default is 1,000 (open-decisions.md 40).
   */
  bodyMaxChars: number;
  /** A score at or above this creates topic membership (the row's chips). Replaces scoring.topic_min_score. */
  topicMinScore: Score;
  /**
   * Jev only: the user's own daily message budget, 0..the plan's maximum; null = the plan's maximum. Sent as
   * `ScoreRequest.maxPerDay` and enforced by the scoring proxy (min(plan, budget)); the engine only defers
   * scoring to the next UTC day when the proxy says the budget is used (models.md §7.3).
   */
  dailyBudget: number | null;
  /**
   * The Topic fields this model reads: a subset of its catalogue entry's `topicFields.supported`, always
   * containing 'name'. Saving a field the entry does not support rejects `invalid_input`.
   */
  topicFields: TopicField[];
}

export interface Model {
  id: ModelId;
  kind: ModelKind;
  name: string;
  icon: IconName;
  /** The switch. */
  enabled: boolean;
  disabledReason: 'user' | 'plan' | null;
  /**
   * Order in the list. It decides ONE thing: when several models have scored a message, the row's chips,
   * `messages.best_score` and conditions without `model` show the scores of the FIRST model in this order.
   */
  position: number;
  run: ModelRun;
  options: ModelOptions;
  /** False when the current plan cannot run it (Jev on Free). Such a model does not count as active. */
  entitled: boolean;
  createdAt: EpochMs;
}

export interface ModelPatch {
  name?: string;
  icon?: IconName;
  enabled?: boolean;
  run?: ModelRun;
  options?: Partial<ModelOptions>;
}

/** Result of `models.test`: what a run-rule draft would have scored, nothing applied. */
export interface ModelRunTestResult {
  /** Live messages received in the last 7 days that were examined (the entry triggers' folders). */
  evaluated: number;
  matched: number;
  /** `matched` scaled to one day: the expected daily load on the model. */
  perDay: number;
  /** Up to 50 rows with the outcome each would have had. */
  rows: Array<{ row: MessageRow; result: 'match' | 'no-match' | 'unknown' }>;
}

// ── Scores per model (synced table `message_scores`) ───────────────────────────────────────────

export type ModelScoreState = 'pending' | 'done' | 'failed';

/** Why a pending score is not being produced right now, on THIS node. null = it is simply queued. */
export type ModelWaitReason =
  | 'download' // an on-device model whose files are not installed on this device
  | 'error' // an on-device model whose install stopped with an error on this device (a failed download, a damaged
  //           file, or installed files whose session cannot start): it waits for Retry (models.md §6.3; review M-R2)
  | 'device' // an on-device model on a node that can never run one (the server; an unsupported browser)
  | 'offline' // a remote model while offline
  | 'unavailable' // a remote model's service did not answer; retried with back-off, failed after scoring.max_attempts
  | 'budget' // Jev: the user's daily budget or the plan's daily maximum is used up until the next UTC day
  | 'plan' // the plan does not include the model any more
  | 'power'; // on-device scoring paused to save battery

export interface MessageModelScore {
  modelId: ModelId;
  state: ModelScoreState;
  /** Full vector keyed by TopicId; null until done (a re-request keeps the previous vector until replaced). */
  scores: Record<string, Score> | null;
  bestScore: Score | null;
  bestTopicId: TopicId | null;
  scoredAt: EpochMs | null;
  /** `ModelCatalogEntry.version` at scoring time. */
  modelVersion: string | null;
  /** When the run rule fired. */
  requestedAt: EpochMs;
  /** Only meaningful while pending; computed for the node answering, not stored. */
  waiting: ModelWaitReason | null;
}

/** `messages.rescore` second argument. */
export interface RescoreOptions {
  /**
   * Absent: every enabled model that has a `request` trigger and whose condition matches.
   * Present: exactly these models (must be enabled and entitled); their condition is NOT consulted —
   * the person asked for precisely this.
   */
  modelIds?: ModelId[];
}

// ── Conditions ─────────────────────────────────────────────────────────────────────────────────

/**
 * The writing system of title + excerpt (kernel/text.ts `detectScript`): 'cyrillic' / 'latin' when at
 * least half of the letters are of that script, else 'other'. Deterministic, no model.
 * Allowed in filters, run rules and scoring rules. Not allowed in views in this round (no SQL column).
 */
export type Script = 'latin' | 'cyrillic' | 'other';
export interface ScriptCondition {
  type: 'condition';
  field: 'script';
  operator: SetOperator;
  values: Script[];
}

/** What the matcher needs to evaluate score conditions that name a model (`MatchSnapshot.modelScores`). */
export interface MatchModelScore {
  modelId: ModelId;
  state: ModelScoreState;
  scores: Readonly<Record<string, Score>> | null;
  bestScore: Score | null;
}

// ── Engine API (becomes `Engine.models`; flip 2) ───────────────────────────────────────────────

export interface ModelsApi {
  /** The catalogue this build ships. Read access. */
  catalog(): Promise<ModelCatalog>;
  /** One Model per catalogue entry, in `position` order. Read access. */
  list(): Promise<Model[]>;
  get(id: ModelId): Promise<Model>;
  /**
   * Change the switch, the run rule or the options. Full access.
   *  - enabling: `not_entitled` when the plan cannot run it; `plan_limit` ('active_models') when the
   *    plan's number of active models is reached. Then queues the unrouted backlog (models.md §7.6).
   *  - disabling: pending scores of the model are withdrawn so nothing waits for it.
   *  - run: validated like a rule (`invalid_input` with dot paths); options are range-checked.
   */
  update(id: ModelId, patch: ModelPatch): Promise<Model>;
  /** New order; `ids` must be exactly the catalogue's ids. Recomputes the shown scores of messages scored by more than one model. */
  reorder(ids: ModelId[]): Promise<void>;
  /** Dry run of a run-rule draft over recent messages. Read access. */
  test(id: ModelId, run: ModelRun): Promise<ModelRunTestResult>;
  /**
   * "Use the recommended set-up": every model gets its catalogue `defaults` (switch, order, run rule,
   * options — except the person's `dailyBudget` and `bodyMaxChars`, which stay theirs) and the seeded ladder
   * rows are restored like `rules.resetLadder`. One undoable step.
   */
  applyRecommended(): Promise<MutationResult>;
  /**
   * Queue (re)scoring by one model for the current members of a scope, in the background. Organise
   * access. Bounded by `scoring.rescore_max` per call; returns how many were queued.
   */
  rescore(id: ModelId, scope: ListScope): Promise<MutationResult>;

  // This device only (never synced). On a runtime without `onDeviceModels` these reject `unsupported`.
  /** Install state of every on-device catalogue model on this device. */
  installs(): Promise<ModelInstallState[]>;
  /** Start or resume the download. Resolves at once with the new state; progress arrives as `model` events. */
  download(id: ModelId): Promise<ModelInstallState>;
  pauseDownload(id: ModelId): Promise<ModelInstallState>;
  /** Stop and delete the partial files. */
  cancelDownload(id: ModelId): Promise<ModelInstallState>;
  /** Delete the installed files from this device. The record, its rule and all scores stay. */
  removeDownload(id: ModelId): Promise<ModelInstallState>;
}

/** Added to `EngineEvent` (flip 4). Progress events are throttled to at most 4 per second per model. */
export type ModelEngineEvent = { type: 'model'; install: ModelInstallState };

/** Added to `PipelineStatus` as `models` (flip 5): one entry per enabled model. */
export interface PipelineModelStatus {
  modelId: ModelId;
  /** Messages whose score by this model is owed. */
  pending: number;
  /** The reason nothing is being scored right now, if any. */
  waiting: ModelWaitReason | null;
  /** Scores of this model that failed for good. */
  failed: number;
  /** Jev: messages scored today for the account and the effective daily ceiling; null otherwise. */
  usedToday: number | null;
  maxPerDay: number | null;
}

// ── The on-device seam (injected; packages/core never imports a runtime) ───────────────────────

export type ModelInstallPhase =
  | 'absent' // nothing on this device
  | 'downloading'
  | 'paused' // partial files kept; `download` resumes with a Range request
  | 'verifying' // SHA-256 of the downloaded files
  | 'installed' // every file present and verified
  | 'error'
  | 'unsupported'; // this runtime cannot run the model at all

export interface ModelInstallState {
  modelId: ModelId;
  phase: ModelInstallPhase;
  /** Revision of the installed or downloading files. A catalogue with another revision = 'absent' + stale files removed. */
  revision: string | null;
  bytesDone: number;
  bytesTotal: number;
  /** The session is in memory now (it is unloaded after an idle period and reloaded on demand). */
  loaded: boolean;
  /** False when the browser may delete the files (storage not persistent) or keeps them only in memory. */
  durable: boolean;
  /** The files were installed before and are gone: the browser evicted them. */
  evicted: boolean;
  error: { code: 'network' | 'integrity' | 'quota' | 'storage' | 'runtime'; message: string } | null;
}

/**
 * What a platform provides to run embedding models. Web: packages/web/src/platform/model/ (a dedicated
 * worker with onnxruntime-web WASM, files in OPFS). Android / iOS later: a native plugin behind the same
 * interface. Server: none — `EngineDeps.onDeviceModels = null`.
 *
 * The runtime owns FILES and INFERENCE only. Which messages, which texts, pooling of topic texts,
 * calibration, storage of vectors and scores all live in the engine (core/src/scoring/local).
 *
 * Invariants
 *  - Files come only from `${catalog.servedFrom}${id}/${revision}/${path}` on the app's own origin
 *    (native: the same URL), are verified against `ModelFile.sha256` before first use, and are never
 *    resolved from a model hub at run time.
 *  - `embed` returns one L2-normalised Float32Array of `recipe.dims` per text, in order, having applied
 *    the given prefix, truncation to `recipe.maxTokens` and the pooling. It loads the model when needed
 *    and rejects `model_unavailable` when the files are not installed (never downloads by itself). When
 *    the files are installed but the session cannot be created (memory, an old browser), the phase becomes
 *    'error' (`error.code = 'runtime'`, the files kept) and `embed` rejects `model_unavailable` without
 *    trying again until `download` (Retry) or a restart — never a silent retry on every call.
 *  - One inference at a time. Calls are queued in order.
 *  - `subscribe` listeners get every state change and download progress (throttled by the engine to 4 a
 *    second). After the last progress of a burst the runtime announces its state once more (web: 300 ms
 *    later), so the engine's throttle never swallows the number a stalled download stopped at.
 */
export interface OnDeviceModelRuntime {
  state(entry: EmbeddingCatalogEntry): Promise<ModelInstallState>;
  /** Start or resume. Resolves when the download has STARTED (or is already installed); completion comes by event. */
  download(entry: EmbeddingCatalogEntry): Promise<ModelInstallState>;
  pause(id: ModelId): Promise<ModelInstallState>;
  cancel(id: ModelId): Promise<ModelInstallState>;
  remove(id: ModelId): Promise<ModelInstallState>;
  embed(entry: EmbeddingCatalogEntry, texts: string[], side: 'query' | 'passage'): Promise<Float32Array[]>;
  /** Release the session's memory now (the engine calls it after `local_model.idle_unload_seconds`). */
  unload(id: ModelId): Promise<void>;
  subscribe(listener: (state: ModelInstallState) => void): () => void;
}

// ── Wiring of the pure scoring arithmetic (core/src/scoring/local/*; no runtime, no SQL) ───────

/** A topic as an embedding model sees it: the centroid of its text vectors and how many texts made it. */
export interface TopicVector {
  topicId: TopicId;
  vec: Float32Array;
  texts: number;
}

/**
 * scoring/local/calibrate.ts exports `anchoredShare`:
 *   c_t      = dot(message, topic.vec) + (topic.texts < fewTextsBelow ? fewTextsBoost : 0)
 *   share_t  = e^(c_t/T) / (e^(c_t/T) + Σ_anchors e^(c_a/T))
 *   score_t  = round(100 · share_t · e^((c_t − max_u c_u)/T))            (0..100 integers)
 * Returns one score per topic, in order.
 */
export type AnchoredShare = (message: Float32Array, topics: readonly TopicVector[], anchors: readonly Float32Array[], calibration: AnchoredShareCalibration) => Score[];

/**
 * scoring/local/texts.ts exports `topicTexts` (models.md §5.0–5.1): the texts an on-device model embeds
 * for a topic, from the fields the model is set to read, each cut to the model's limits —
 *   every phrase, when 'phrases' is read and the topic has any;
 *   the description as ONE more text, when 'description' is read and it is not blank;
 *   `[name]` when those produce nothing (the name is the fallback and is always read).
 * So with the default (name + phrases) a topic with phrases is embedded from its phrases alone — the
 * measured recipe — and a topic without phrases from its name.
 */
export type TopicTexts = (
  topic: { name: string; hint: string; phrases: readonly string[] },
  fields: readonly TopicField[],
  limits: ModelTopicFields['limits'],
) => string[];

/**
 * scoring/local/texts.ts also exports `topicTextSignature`: a stable hash of exactly what `topicTexts`
 * would return for (topic, fields, limits). It is the key of a model's cached topic vector and, for Jev,
 * of the proxy's score cache — so editing a field a model does not read invalidates nothing of that model.
 */
export type TopicTextSignature = (
  topic: { name: string; hint: string; phrases: readonly string[] },
  fields: readonly TopicField[],
  limits: ModelTopicFields['limits'],
) => string;

/** scoring/local/texts.ts exports `messageText`: `${title}\n\n${first bodyMaxChars of htmlToText(bodyHtml)}` trimmed. */
export type MessageText = (message: { title: string | null; bodyHtml: string | null }, bodyMaxChars: number) => string;

/** scoring/local/vectors.ts exports `packVector` / `unpackVector`: Float32 ↔ little-endian int16 (× 32767) BLOB. */
export type PackVector = (vec: Float32Array) => Uint8Array;
export type UnpackVector = (blob: Uint8Array) => Float32Array;

// ── Sync (flip 1) ──────────────────────────────────────────────────────────────────────────────

/** The data schema that introduced the tables below (equals contracts/sync.ts `SCHEMA_VERSION` today). */
export const SCHEMA_VERSION_MODELS = 3 as const;

/** Appended to SYNC_TABLES (contracts/sync.ts). `topics` additionally gained the column 'phrases' in its row register. */
export const MODEL_SYNC_TABLES = [
  {
    table: 'models',
    key: ['id'],
    registers: [{ name: 'row', hlc: 'hlc', columns: ['name', 'icon', 'enabled', 'disabled_reason', 'position', 'run', 'options', 'created_at', 'deleted_at'] }],
  },
  {
    table: 'message_scores',
    key: ['message_id', 'model_id'],
    registers: [{ name: 'row', hlc: 'hlc', columns: ['state', 'scores', 'best_score', 'best_topic_id', 'scored_at', 'model_version', 'requested_at', 'deleted_at'] }],
    parentMessageColumn: 'message_id',
  },
] as const satisfies readonly SyncTableSpec[];

// ── Plans (flip 8) ─────────────────────────────────────────────────────────────────────────────

/**
 * New `LimitName` member. (The user's own daily Jev budget is reported as `scored_per_day` with
 * `details.budget = true`, the name quick controls deployed; there is no separate 'jev_daily_budget'.)
 */
export type ModelLimitName = 'active_models';

/** New `ErrorCode` member: the on-device model is not installed / cannot load on this device. Retryable. */
export type ModelErrorCode = 'model_unavailable';

// ── MCP (flip 10) ──────────────────────────────────────────────────────────────────────────────

/** A model as an assistant sees it. */
export interface McpModel {
  id: string;
  name: string;
  kind: ModelKind;
  enabled: boolean;
  /** False = the plan cannot run it. */
  entitled: boolean;
  position: number;
  run: ModelRun;
  options: ModelOptions;
  /** Names of the scoring rules that test this model's scores, in evaluation order. */
  actionsOnScoring: string[];
  quality: ModelQuality;
  /** Messages waiting for this model, and why (the server reports 'device' for on-device models). */
  pending: number;
  waiting: ModelWaitReason | null;
}

/** New tools, merged into `McpTools`. */
export interface McpModelTools {
  list_models: { access: 'read'; input: Record<string, never>; output: { models: McpModel[] } };
  /** Change a model's switch, run rule or options. A model cannot be created or deleted over MCP. */
  update_model: {
    access: 'full';
    input: { id: string; enabled?: boolean; run?: ModelRun; options?: Partial<ModelOptions> };
    output: McpModel;
  };
}

/**
 * Widened shapes of existing tools (replace the declarations in mcp.ts):
 *  - `upsert_topic.input` += `phrases?: string[]`  (short paraphrases for on-device models; Jev ignores them)
 *  - `rescore_messages.input` += `models?: string[]`; output becomes `McpOk & { waitingForDevice: number }`
 *  - `McpMessageSummary` += `scoredBy: string | null` (model name); `McpMessageFull` += `scoresByModel:
 *    Record<string, Record<string, number>>` (model name → topic name → score) and `waitingFor: string[]`
 *  - `McpCorrection` += `scoresByModelAtDecision: Record<string, Record<string, number>> | null`
 *  - `get_configuration.output` += `models: McpModel[]`
 *  - `get_overview.output` += `pendingOnDevice: number`
 */
export interface McpModelPrompts {
  /** Writes 5–8 short paraphrases for every topic that has none and saves them with `upsert_topic`. */
  write_topic_phrases: { language?: string };
}

// ── Message ids helper for the bulk rescore result ─────────────────────────────────────────────

/** `MutationResult.changed` of `models.rescore` = messages queued; this is what the UI toast shows. */
export interface ModelRescoreReport {
  queued: number;
  /** Message ids, when at most 500. */
  messageIds?: MessageId[];
}
